import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useMutation } from 'react-query';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import useStepOrchestratorNextStep from '@savgroup-front-common/core/molecules/StepsOrchestrator/hooks/useStepOrchestratorNextStep';
import { OwnerService } from '@/api';
import { ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE } from '@/api/Owner/getOrderByOrderReferenceAndOrderPrice';
import { CreateClaimContext, CreateClaimValues } from '@/types';

import messages from '../messages';

import PhoneNumberLoginEnableSchema from './PhoneNumberLoginEnable.schema';
import { PhoneNumberLoginEnableValues } from './PhoneNumberLoginEnable.types';

const GetOrderByOrderReferenceAndOrderPrice =
  'getOrderByOrderReferenceAndOrderPrice';

interface UseCustomerInvoiceInformationsAgrs {
  values: CreateClaimValues;
}

interface UseCustomerInvoiceInformationsReturnValues {
  formContext: UseFormReturn<PhoneNumberLoginEnableValues>;
  isLoading: boolean;
  onSubmit: SubmitHandler<PhoneNumberLoginEnableValues>;
}

const usePhoneNumberLoginEnable = ({
  values,
}: UseCustomerInvoiceInformationsAgrs): UseCustomerInvoiceInformationsReturnValues => {
  const { pushErrors } = useToasts({
    extendedMessages: messages,
  });
  const { sellerId } = values;
  const nextStep = useStepOrchestratorNextStep<
    CreateClaimValues,
    CreateClaimContext
  >();

  const formContext = useForm<PhoneNumberLoginEnableValues>({
    resolver: yupResolver(PhoneNumberLoginEnableSchema),
    defaultValues: {
      orderReference: values.orderReference || '',
      phoneNumber: values.PhoneNumberLastFourDigits || '',
    },
  });

  const { watch } = formContext;

  const orderReference = watch('orderReference');
  const phoneNumber = watch('phoneNumber');

  const { mutateAsync: handleSubmitOrderInformations, isLoading } = useMutation(
    [
      GetOrderByOrderReferenceAndOrderPrice,
      { sellerId, orderReference, phoneNumber },
    ],
    async () => {
      if (!sellerId || !orderReference || !phoneNumber) {
        return undefined;
      }

      const response = await OwnerService.getOrderByOrderReferenceAndOrderPrice(
        {
          orderReference,
          PhoneNumberLastFourDigits: phoneNumber,
          sellerId,
        },
      );

      if (response.failure) {
        pushErrors(response.errors);

        logError(
          `Error with orderReference ${response.errors.map((e) => e.message)}`,
        );

        return undefined;
      }

      return response.value;
    },
  );

  const onSubmit: SubmitHandler<PhoneNumberLoginEnableValues> =
    useCallback(async () => {
      const response = await handleSubmitOrderInformations();

      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ORDER_ALREADY_IMPORT
      ) {
        window.location = response?.irshUrl as unknown as string & Location;

        return undefined;
      }
      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.CUSTOMER_IDENTIFIED
      ) {
        nextStep({
          ...values,
          PhoneNumberLastFourDigits: phoneNumber,
          orderReference,
          owner: values.owner ? values.owner : response?.owner,
        });

        return undefined;
      }
      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ANONYMOUS_CUSTOMER
      ) {
        nextStep({
          ...values,
          PhoneNumberLastFourDigits: phoneNumber,
          orderReference,
        });

        return undefined;
      }

      return undefined;
    }, [
      handleSubmitOrderInformations,
      orderReference,
      phoneNumber,
      nextStep,
      values,
    ]);

  return {
    formContext,
    isLoading,
    onSubmit,
  };
};

export default usePhoneNumberLoginEnable;
